import { useEffect, useState, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import baseURL from "./components/authComponents/baseURL/BaseURL";
import { useDispatch } from "react-redux";
import { authSliceActions } from "./redux/slices/authSlice";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Critical path — eagerly loaded
import HomePage from "./pages/HomePage";
import Navbar from "./components/shared/Navbar";
import Footer from "./components/shared/Footer";
import loginToken from "./components/authComponents/localStorageItems/LoginTokin";
import LoginPopUp from "./components/authComponents/LoginPopUp";
import PakistanHomePage from "./components/homepage/pakistan/PakistanHomePage";
import { GeoProvider, useGeo } from "./utils/GeoContext";
import { SiteSettingsProvider, useSiteSettings } from "./utils/SiteSettingsContext";
import useVisitorHeartbeat from "./hooks/useVisitorHeartbeat";
import { captureUtmParams } from "./utils/utmTracker";
import { captureRefFromUrl } from "./utils/partnerRef";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import { trackGuestPage } from "./utils/returnTo";

// Lazy-loaded routes (not needed on first paint)
const LoginScreen = lazy(() => import("./components/authComponents/Login"));
const Profile = lazy(() => import("./pages/Profile"));
const ContactUs = lazy(() => import("./components/homepage/contact-us/ContactUs"));
const AboutUs = lazy(() => import("./components/homepage/about-us/AboutUs"));
const OtherProfiles = lazy(() => import("./components/homepage/profile/OtherProfileView"));
const NotFoundPage = lazy(() => import("./components/errorPage/NotFound"));
const TermConditions = lazy(() => import("./pages/TermConditions"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicy"));
const ForgotPasswordScreen = lazy(() => import("./components/authComponents/ForgotPassword"));
const ResetPasswordScreen = lazy(() => import("./components/authComponents/ResetPassword"));
const AllProfiles = lazy(() => import("./components/homepage/all-profiles/AllProfiles"));
const LoginViaOTP = lazy(() => import("./components/authComponents/LoginViaOTP"));
const OTPsetter = lazy(() => import("./components/authComponents/OTPsetter"));
const EditInformationForm = lazy(() => import("./components/authComponents/EditInformationForm"));
const UpdatePassword = lazy(() => import("./components/authComponents/UpdatePassword"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const SingleBlogPage = lazy(() => import("./components/homepage/blog-Screen/SingleBlogPage"));
const CategoryBlogPage = lazy(() => import("./components/homepage/blog-Screen/CategoryBlogPage"));
const RefundPolicy = lazy(() => import("./components/homepage/refundPolicy/RefundPolicy"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const MyPlan = lazy(() => import("./pages/MyPlan"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ShortlistPage = lazy(() => import("./pages/ShortlistPage"));
const ProfileViewsPage = lazy(() => import("./pages/ProfileViewsPage"));
const HowWeAreDifferent = lazy(() => import("./components/homepage/HowWeAreDifferent"));
const WhyWeRejectProfiles = lazy(() => import("./components/homepage/WhyWeRejectProfiles"));
const CreateAccount = lazy(() => import("./pages/Authentication/CreateAccount.tsx"));
const EditProfile = lazy(() => import("./components/homepage/profile/editProfile/EditProfile.tsx"));
const Maintenance = lazy(() => import("./pages/maintenance/Maintenance"));
const CompatibilityTestOverlay = lazy(() => import("./components/compatibility/CompatibilityTestOverlay"));
const CompatibilityNotification = lazy(() => import("./components/compatibility/CompatibilityNotification"));
const VerifyEmailPage = lazy(() => import("./components/authComponents/emailVerification/VerifyEmailPage"));
const FreePlanBanner = lazy(() => import("./components/plans/FreePlanBanner"));
const FeedbackWidget = lazy(() => import("./components/shared/FeedbackWidget"));
const ViewProfilesFloat = lazy(() => import("./components/shared/ViewProfilesFloat"));
const SupportWidget = lazy(() => import("./components/shared/SupportWidget"));
const CancellationPolicyPage = lazy(() => import("./pages/CancellationPolicy"));
const ChildSafetyPage = lazy(() => import("./pages/ChildSafety"));
const CityLandingPage = lazy(() => import("./components/homepage/city-landing/CityLandingPage"));
const KeywordLandingPage = lazy(() => import("./components/homepage/keyword-landing/KeywordLandingPage"));
const VerifyProfilePage = lazy(() => import("./pages/VerifyProfilePage"));

const AdminSettings = lazy(() => import("./components/adminSideComponents/settings/index"));
const AdminDashboard = lazy(() => import("./components/adminSideComponents/dashboard/index"));
const Users = lazy(() => import("./components/adminSideComponents/users"));
const AllCountries = lazy(() => import("./components/adminSideComponents/countries"));
const AllStates = lazy(() => import("./components/adminSideComponents/states"));
const AllCities = lazy(() => import("./components/adminSideComponents/cities"));
const EditAnnouncementForm = lazy(() => import("./components/adminSideComponents/Announcement/edit_announcement"));
const BlogsIndex = lazy(() => import("./components/adminSideComponents/blogs/blogsIndex"));
const BlogCategoriesIndex = lazy(() => import("./components/adminSideComponents/blogs/BlogCategoriesIndex"));
const BlogFormIndex = lazy(() => import("./components/adminSideComponents/blogs/BlogFormIndex"));
const EditBlogFormIndex = lazy(() => import("./components/adminSideComponents/blogs/EditBlogFormIndex"));
const Emails = lazy(() => import("./components/adminSideComponents/emails/Emails"));
const Referrals = lazy(() => import("./components/adminSideComponents/refferals/Refferals"));
const EditPricing = lazy(() => import("./components/adminSideComponents/pricing/EditPricing"));
const ChildAdminsPage = lazy(() => import("./components/adminSideComponents/childAdmins"));
const ClaimPromo = lazy(() => import("./components/authComponents/ClaimPromo"));
const PromoCodes = lazy(() => import("./components/adminSideComponents/promoCodes/PromoCodes"));
const PartnersPage = lazy(() => import("./components/adminSideComponents/partners"));
const PartnerApply = lazy(() => import("./pages/Partner/PartnerApply"));
const PartnerLogin = lazy(() => import("./pages/Partner/PartnerLogin"));
const PartnerDashboard = lazy(() => import("./pages/Partner/PartnerDashboard"));
const PartnerLanding = lazy(() => import("./pages/Partner/PartnerLanding"));
const DailyLogPage = lazy(() => import("./components/adminSideComponents/dailyLog/index"));
const ProfileCardsPage = lazy(() => import("./components/adminSideComponents/profileCards/index"));
const IncompleteSignupsPage = lazy(() => import("./components/adminSideComponents/incompleteSignups/index"));
const TopViewersPage = lazy(() => import("./components/adminSideComponents/topViewers/index"));
const ConductPage = lazy(() => import("./components/adminSideComponents/conduct/index"));
const RenewalsPage = lazy(() => import("./components/adminSideComponents/renewals/index"));
const PendingPayments = lazy(() => import("./components/adminSideComponents/payments/PendingPayments"));
const TitleApprovalsPage = lazy(() => import("./components/adminSideComponents/titleApprovals/index"));
const SupportPage = lazy(() => import("./components/adminSideComponents/support/index"));
const FeaturedAdminPage = lazy(() => import("./components/adminSideComponents/featured/index"));
const SupportMobileApp = lazy(() => import("./pages/SupportApp/SupportApp"));
const AdminEditProfile = lazy(() => import("./components/homepage/profile/editProfile/editProfileByAdmin.tsx"));
// Smart home page that switches based on region
// No loading gate — shows content instantly, swaps to PK when geo resolves.
const SmartHomePage = ({ setLoginPopUp }) => {
  const { isPakistan } = useGeo();
  return isPakistan ? (
    <PakistanHomePage setLoginPopUp={setLoginPopUp} />
  ) : (
    <HomePage setLoginPopUp={setLoginPopUp} />
  );
};

function App() {
  useVisitorHeartbeat();
  const location = useLocation();
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("");
  const [isIncomepleteProfile, setIsIncompleteProfile] = useState(false);
  const [userStatus, setUserStatus] = useState("");
  const [canRequestReview, setCanRequestReview] = useState(false);
  const [userId, setUserID] = useState(false);
  const [showLoginPopUp, setLoginPopUp] = useState(false);
  const [hasCompatibilityForm, setHasCompatibilityForm] = useState(undefined);
  // Email verification was retired when login moved to emailed one-time codes.
  const [, setEmailVerified] = useState(true);
  const [activePlan, setActivePlan] = useState(null);
  const IS_MAINTENANCE_MODE = false;

  // Capture UTM params from landing URL on first load
  useEffect(() => {
    captureUtmParams();
    captureRefFromUrl();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Keep staff traffic out of GA4: admin pages were the 4th-largest "landing
  // page" and even logged sign_up events. Once a browser has been used by an
  // admin (or opened /admin), it is flagged and index.html disables GA4 on
  // every later visit too. Ads/Meta tags are untouched.
  useEffect(() => {
    const isStaff =
      userRole === "admin" ||
      userRole === "child_admin" ||
      location.pathname.startsWith("/admin");
    if (!isStaff) return;
    window["ga-disable-G-6SZX2KE2RG"] = true;
    try {
      localStorage.setItem("sp_internal_traffic", "1");
    } catch {
      /* storage disabled */
    }
  }, [userRole, location.pathname]);
  const dispatch = useDispatch();
  useEffect(() => {
    let statusRef = "";
    const getRole = async () => {
      try {
        const response = await baseURL.get("/profile", {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        });
        setUserRole(response.data.data.role);
        setUserStatus(response.data.data.status);
        dispatch(authSliceActions.getStatus(response.data.data.status));
        statusRef = response.data.data.status;
        setCanRequestReview(response.data.data.can_request_review);
        setUserID(response.data.data.id);
        setIsIncompleteProfile(response.data.data.is_incomplete_profile);
        setHasCompatibilityForm(response.data.data.has_compatibility_form);
        setActivePlan(response.data.data.active_plan);
        if (response.data.data.email_verified !== undefined) {
          setEmailVerified(response.data.data.email_verified);
        }
      } catch (error) {
        console.error(error);
      }
    };

    // Approval is live: while the member is still pending, re-check their
    // status every 30s and whenever the tab regains focus, so an approval in
    // the admin panel shows up without a manual refresh.
    let timer = null;
    const refreshIfPending = () => {
      if (document.visibilityState === "hidden") return;
      if (statusRef && statusRef.toLowerCase() === "active") return;
      getRole();
    };
    if (loginToken) {
      getRole();
      timer = setInterval(refreshIfPending, 30000);
      window.addEventListener("focus", refreshIfPending);
      document.addEventListener("visibilitychange", refreshIfPending);
    }
    return () => {
      if (timer) clearInterval(timer);
      window.removeEventListener("focus", refreshIfPending);
      document.removeEventListener("visibilitychange", refreshIfPending);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Logged-out visitors: remember the current page so logging in returns
  // them here (e.g. a shared profile link) instead of the home page.
  useEffect(() => {
    if (!loginToken) trackGuestPage(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (
      loginToken &&
      (location.pathname === "/login" ||
        location.pathname === "/signup" ||
        location.pathname === "/otpsender" ||
        location.pathname === "/setotp")
    ) {
      if (location.pathname === "/signup") {
        toast.info("You're already logged in! Redirecting to your account.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
      navigate("/");
    }
  }, [location.pathname, navigate]);

  // Legacy payment gate removed — the Free plan is now open to all members
  // (Pakistan males get 0 contact views instead of being blocked).
  useEffect(() => {
    localStorage.removeItem("payment_required");
  }, []);
  const hideNavbarFooterPaths = [
    "/signup",
    "/create-account",
    "/edit-profile",
  ];
  // const hideNavbarFooter = hideNavbarFooterPaths.includes(location.pathname);
  // Check if it's an admin page
  const isAdminPage = location.pathname.startsWith("/admin/");

  // Check if it's one of the public pages (like /login)
  const isPublicHidePage = hideNavbarFooterPaths.includes(location.pathname);

  // Hide the navbar if *either* is true
  const hideNavbarFooter = isAdminPage || isPublicHidePage || location.pathname.startsWith("/support-app");

  // Hide WhatsApp icon on admin pages
  // const hideWhatsAppPaths = [
  //   "/admin/dashboard",
  //   "/admin/users",
  //   "/admin/refferals",
  //   "/admin/emails",
  //   "/admin/settings",
  //   "/admin/countries",
  //   "/admin/states",
  //   "/admin/cities",
  //   "/admin/edit-announcement",
  //   "/admin/edit-pricing",
  // ];
  // const showWhatsApp = !hideWhatsAppPaths.includes(location.pathname);
  // Mobile support app: full-screen, no navbar/footer, no floating widget.
  const isSupportApp = location.pathname.startsWith("/support-app");
  const showWhatsApp = !isAdminPage;

  return (
    <>
      {!hideNavbarFooter && (
        <Navbar
          userRole={userRole}
          userStatus={userStatus}
          canRequestReview={canRequestReview}
          userId={userId}
          setLoginPopUp={setLoginPopUp}
          isIncomepleteProfile={isIncomepleteProfile}
        />
      )}
      {showLoginPopUp && <LoginPopUp onClose={() => setLoginPopUp(false)} />}
      {loginToken &&
        !hideNavbarFooter &&
        userRole !== "admin" &&
        activePlan === "free" &&
        hasCompatibilityForm !== undefined && (
          <div className="max-w-5xl mx-auto px-4 mt-2">
            <Suspense fallback={null}>
              <FreePlanBanner hasCompatibilityForm={hasCompatibilityForm} />
            </Suspense>
          </div>
        )}
      <Suspense fallback={<div className="flex justify-center items-center min-h-[60vh]"><div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200" style={{borderTopColor:'#8B1E1E'}} /></div>}>
      {IS_MAINTENANCE_MODE ? (
        <Routes>
          <Route path="/" element={<SmartHomePage setLoginPopUp={setLoginPopUp} />} />
          <Route path="*" element={<Maintenance />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/" element={<SmartHomePage setLoginPopUp={setLoginPopUp} />} />
          <Route
            path="/login"
            element={loginToken ? <Navigate to="/" replace /> : <LoginScreen />}
          />
          <Route
            path="/otpsender"
            element={loginToken ? <Navigate to="/" replace /> : <LoginViaOTP />}
          />
          <Route
            path="/setotp"
            element={loginToken ? <Navigate to="/" replace /> : <OTPsetter />}
          />
          <Route
            path="/signup"
            element={
              loginToken ? <Navigate to="/" replace /> : <CreateAccount />
            }
          />
          {/* Old Signup Page  */}
          {/* <Route
            path="/create-account"
            element={
              loginToken ? <Navigate to="/" replace /> : <CreateAccount />
            }
          /> */}
          
          <Route
            path="/profile"
            element={
              loginToken ? (
                <Profile setLoginPopUp={setLoginPopUp} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          
          {/* New Edit Profile Page */}
          <Route
            path="/edit-profile"
            element={
              loginToken ? (
                <EditProfile />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/edit-profile-admin"
            element={
              loginToken ? (
                <Suspense fallback={null}><AdminEditProfile /></Suspense>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/profile-details/:id"
            element={<OtherProfiles />}
          />
          {/* <Route path="/blogs">
            <Route index element={<BlogPage />} />
            <Route path="success-story" element={<SucessStoryPage />} />
            <Route path="first-blog" element={<FirstBlog />} />
            <Route path="second-blog" element={<SecondBlog />} />
            <Route path="third-blog" element={<ThirdBlog />} />
            <Route path="fourth-blog" element={<FourthBlog />} />
          </Route> */}
          {/* <Route path="/blogs">
            <Route index element={<BlogPage />} /> 
            <Route path=":slug" element={<SingleBlogPage />} /> 
          </Route> */}
          <Route path="/blogs">
            <Route index element={<BlogPage />} />
            <Route path="category/:slug" element={<CategoryBlogPage />} />

            <Route path=":slug" element={<SingleBlogPage />} />
          </Route>
          <Route path="/updatepassword" element={<UpdatePassword />} />

          <Route path="/edit-profile" element={<EditInformationForm />} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/claim/:token" element={<Suspense fallback={null}><ClaimPromo /></Suspense>} />
          <Route path="/termAndConditions" element={<TermConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
          <Route path="/child-safety" element={<ChildSafetyPage />} />
          <Route path="/all-profiles" element={<AllProfiles />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/my-plan" element={<MyPlan />} />
          <Route
            path="/my-shortlist"
            element={
              loginToken ? (
                <ShortlistPage />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/who-viewed-me"
            element={loginToken ? <ProfileViewsPage /> : <Navigate to="/login" replace />}
          />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/how-we-are-different" element={<HowWeAreDifferent />} />
          <Route path="/why-we-reject-profiles" element={<WhyWeRejectProfiles />} />
          {/* Explicit city landing page routes (React Router v6 doesn't support partial segment params like /rishta-:slug) */}
          <Route path="/rishta-lahore" element={<CityLandingPage slug="lahore" />} />
          <Route path="/rishta-karachi" element={<CityLandingPage slug="karachi" />} />
          <Route path="/rishta-islamabad" element={<CityLandingPage slug="islamabad" />} />
          <Route path="/rishta-rawalpindi" element={<CityLandingPage slug="rawalpindi" />} />
          <Route path="/rishta-faisalabad" element={<CityLandingPage slug="faisalabad" />} />
          <Route path="/rishta-multan" element={<CityLandingPage slug="multan" />} />
          <Route path="/rishta-peshawar" element={<CityLandingPage slug="peshawar" />} />
          <Route path="/rishta-quetta" element={<CityLandingPage slug="quetta" />} />
          <Route path="/rishta-sialkot" element={<CityLandingPage slug="sialkot" />} />
          <Route path="/rishta-gujranwala" element={<CityLandingPage slug="gujranwala" />} />
          {/* New city landing pages — July 2026 */}
          <Route path="/rishta-abbottabad" element={<CityLandingPage slug="abbottabad" />} />
          <Route path="/rishta-bahawalpur" element={<CityLandingPage slug="bahawalpur" />} />
          <Route path="/rishta-hyderabad" element={<CityLandingPage slug="hyderabad" />} />
          <Route path="/rishta-sargodha" element={<CityLandingPage slug="sargodha" />} />
          <Route path="/rishta-sukkur" element={<CityLandingPage slug="sukkur" />} />
          <Route path="/rishta-mardan" element={<CityLandingPage slug="mardan" />} />
          <Route path="/rishta-gujrat" element={<CityLandingPage slug="gujrat" />} />
          <Route path="/rishta-jhelum" element={<CityLandingPage slug="jhelum" />} />
          <Route path="/rishta-sahiwal" element={<CityLandingPage slug="sahiwal" />} />
          <Route path="/rishta-larkana" element={<CityLandingPage slug="larkana" />} />
          <Route path="/rishta-dera-ghazi-khan" element={<CityLandingPage slug="dera-ghazi-khan" />} />
          <Route path="/rishta-mirpur" element={<CityLandingPage slug="mirpur" />} />
          <Route path="/rishta-muzaffarabad" element={<CityLandingPage slug="muzaffarabad" />} />
          <Route path="/rishta-wah-cantt" element={<CityLandingPage slug="wah-cantt" />} />
          <Route path="/rishta-okara" element={<CityLandingPage slug="okara" />} />
          <Route path="/rishta-kasur" element={<CityLandingPage slug="kasur" />} />
          <Route path="/rishta-sheikhupura" element={<CityLandingPage slug="sheikhupura" />} />
          <Route path="/rishta-rahim-yar-khan" element={<CityLandingPage slug="rahim-yar-khan" />} />
          <Route path="/rishta-mingora" element={<CityLandingPage slug="mingora" />} />
          <Route path="/rishta-mansehra" element={<CityLandingPage slug="mansehra" />} />
          <Route path="/rishta-nawabshah" element={<CityLandingPage slug="nawabshah" />} />
          <Route path="/rishta-jhang" element={<CityLandingPage slug="jhang" />} />
          <Route path="/rishta-chiniot" element={<CityLandingPage slug="chiniot" />} />
          <Route path="/rishta-kohat" element={<CityLandingPage slug="kohat" />} />
          <Route path="/rishta-bannu" element={<CityLandingPage slug="bannu" />} />

          {/* Keyword landing pages — caste, profession, sect, general */}
          <Route path="/doctor-rishta" element={<KeywordLandingPage slug="doctor-rishta" />} />
          <Route path="/engineer-rishta" element={<KeywordLandingPage slug="engineer-rishta" />} />
          <Route path="/pakistani-rishta" element={<KeywordLandingPage slug="pakistani-rishta" />} />
          <Route path="/muslim-matrimony" element={<KeywordLandingPage slug="muslim-matrimony" />} />
          <Route path="/overseas-pakistani-rishta" element={<KeywordLandingPage slug="overseas-pakistani-rishta" />} />
          <Route path="/uk-pakistani-rishta" element={<KeywordLandingPage slug="uk-pakistani-rishta" />} />
          <Route path="/usa-pakistani-rishta" element={<KeywordLandingPage slug="usa-pakistani-rishta" />} />
          <Route path="/educated-rishta" element={<KeywordLandingPage slug="educated-rishta" />} />
          <Route path="/free-rishta" element={<KeywordLandingPage slug="free-rishta" />} />
          <Route path="/free-rishta-islamabad" element={<KeywordLandingPage slug="free-rishta-islamabad" />} />
          <Route path="/free-rishta-karachi" element={<KeywordLandingPage slug="free-rishta-karachi" />} />
          <Route path="/free-rishta-lahore" element={<KeywordLandingPage slug="free-rishta-lahore" />} />
          <Route path="/free-rishta-rawalpindi" element={<KeywordLandingPage slug="free-rishta-rawalpindi" />} />
          <Route path="/free-rishta-peshawar" element={<KeywordLandingPage slug="free-rishta-peshawar" />} />
          <Route path="/free-rishta-faisalabad" element={<KeywordLandingPage slug="free-rishta-faisalabad" />} />
          <Route path="/free-rishta-gujranwala" element={<KeywordLandingPage slug="free-rishta-gujranwala" />} />
          <Route path="/free-rishta-multan" element={<KeywordLandingPage slug="free-rishta-multan" />} />
          {/* Overseas + forces landing pages — August 2026 */}
          <Route path="/canada-pakistani-rishta" element={<KeywordLandingPage slug="canada-pakistani-rishta" />} />
          <Route path="/australia-pakistani-rishta" element={<KeywordLandingPage slug="australia-pakistani-rishta" />} />
          <Route path="/saudi-arabia-pakistani-rishta" element={<KeywordLandingPage slug="saudi-arabia-pakistani-rishta" />} />
          <Route path="/dubai-pakistani-rishta" element={<KeywordLandingPage slug="dubai-pakistani-rishta" />} />
          <Route path="/army-rishta" element={<KeywordLandingPage slug="army-rishta" />} />
          <Route path="/arain-rishta" element={<KeywordLandingPage slug="arain-rishta" />} />
          <Route path="/rajput-rishta" element={<KeywordLandingPage slug="rajput-rishta" />} />
          <Route path="/jatt-rishta" element={<KeywordLandingPage slug="jatt-rishta" />} />
          <Route path="/syed-rishta" element={<KeywordLandingPage slug="syed-rishta" />} />
          <Route path="/pathan-rishta" element={<KeywordLandingPage slug="pathan-rishta" />} />
          <Route path="/sheikh-rishta" element={<KeywordLandingPage slug="sheikh-rishta" />} />
          <Route path="/sunni-rishta" element={<KeywordLandingPage slug="sunni-rishta" />} />
          <Route path="/shia-rishta" element={<KeywordLandingPage slug="shia-rishta" />} />
          <Route path="/mughal-rishta" element={<KeywordLandingPage slug="mughal-rishta" />} />
          <Route path="/awan-rishta" element={<KeywordLandingPage slug="awan-rishta" />} />
          <Route path="/gujjar-rishta" element={<KeywordLandingPage slug="gujjar-rishta" />} />
          <Route path="/malik-rishta" element={<KeywordLandingPage slug="malik-rishta" />} />
          <Route path="/butt-rishta" element={<KeywordLandingPage slug="butt-rishta" />} />
          <Route path="/chaudhary-rishta" element={<KeywordLandingPage slug="chaudhary-rishta" />} />

          <Route path="/verify-profile" element={<Suspense fallback={null}><VerifyProfilePage /></Suspense>} />
          <Route path="/all" element={<Navigate to="/all-profiles" replace />} />
          <Route path="/register" element={<Navigate to="/signup" replace />} />
          {/* Admin routes — lazy loaded to keep main bundle small */}
          <Route path="/admin/dashboard" element={<Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#8B1E1E]"></div></div>}><AdminDashboard /></Suspense>} />
          <Route path="/admin/incomplete-signups" element={<Suspense fallback={null}><IncompleteSignupsPage /></Suspense>} />
          <Route path="/admin/top-viewers" element={<Suspense fallback={null}><TopViewersPage /></Suspense>} />
          <Route path="/admin/conduct" element={<Suspense fallback={null}><ConductPage /></Suspense>} />
          <Route path="/admin/renewals" element={<Suspense fallback={null}><RenewalsPage /></Suspense>} />
          <Route path="/admin/pending-payments" element={<Suspense fallback={null}><PendingPayments /></Suspense>} />
          <Route path="/admin/title-approvals" element={<Suspense fallback={null}><TitleApprovalsPage /></Suspense>} />
          <Route path="/admin/support" element={<Suspense fallback={null}><SupportPage /></Suspense>} />
          <Route path="/admin/featured" element={<Suspense fallback={null}><FeaturedAdminPage /></Suspense>} />
          <Route path="/support-app" element={<Suspense fallback={null}><SupportMobileApp /></Suspense>} />
          <Route path="/admin/edit-pricing" element={<Suspense fallback={null}><EditPricing /></Suspense>} />
          <Route path="/admin/users" element={<Suspense fallback={null}><Users /></Suspense>} />
          <Route path="/admin/emails" element={<Suspense fallback={null}><Emails /></Suspense>} />
          <Route path="/admin/refferals" element={<Suspense fallback={null}><Referrals /></Suspense>} />
          <Route path="/admin/settings" element={<Suspense fallback={null}><AdminSettings /></Suspense>} />
          <Route path="/admin/countries" element={<Suspense fallback={null}><AllCountries /></Suspense>} />
          <Route path="/admin/states" element={<Suspense fallback={null}><AllStates /></Suspense>} />
          <Route path="/admin/blogs" element={<Suspense fallback={null}><BlogsIndex /></Suspense>} />
          <Route path="/admin/blogs/new" element={<Suspense fallback={null}><BlogFormIndex /></Suspense>} />
          <Route path="/admin/blogs/edit/:id" element={<Suspense fallback={null}><EditBlogFormIndex /></Suspense>} />
          <Route path="/admin/blog-categories" element={<Suspense fallback={null}><BlogCategoriesIndex /></Suspense>} />
          <Route path="/admin/cities" element={<Suspense fallback={null}><AllCities /></Suspense>} />
          <Route path="/admin/promo-codes" element={<Suspense fallback={null}><PromoCodes /></Suspense>} />
          <Route path="/admin/partners" element={<Suspense fallback={null}><PartnersPage /></Suspense>} />
          <Route path="/partners/apply" element={<Suspense fallback={null}><PartnerApply /></Suspense>} />
          <Route path="/partners/login" element={<Suspense fallback={null}><PartnerLogin /></Suspense>} />
          <Route path="/partners/dashboard" element={<Suspense fallback={null}><PartnerDashboard /></Suspense>} />
          <Route path="/p/:slug" element={<Suspense fallback={null}><PartnerLanding /></Suspense>} />
          <Route path="/admin/child-admins" element={<Suspense fallback={null}><ChildAdminsPage /></Suspense>} />
          <Route path="/admin/daily-log" element={<Suspense fallback={null}><DailyLogPage /></Suspense>} />
          <Route path="/admin/profile-cards" element={<Suspense fallback={null}><ProfileCardsPage /></Suspense>} />
          <Route
            path="/admin/edit-announcement"
            element={<Suspense fallback={null}><EditAnnouncementForm /></Suspense>}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      )}
      </Suspense>
      <ToastContainer autoClose={2500} />
      {!hideNavbarFooter && <Footer />}

      {/* Compatibility Test Overlay (full-screen experience) */}
      <Suspense fallback={null}><CompatibilityTestOverlay /></Suspense>

      {/* Compatibility Notification (prompt for users who haven't taken the test) */}
      <Suspense fallback={null}><CompatibilityNotification
        hasCompatibilityForm={hasCompatibilityForm}
        isLoggedIn={!!loginToken}
      /></Suspense>

      {/* Feedback Widget */}
      {showWhatsApp && !isSupportApp && <Suspense fallback={null}><FeedbackWidget /></Suspense>}

      {/* View Profiles floating CTA */}
      {showWhatsApp && !isSupportApp && <Suspense fallback={null}><ViewProfilesFloat /></Suspense>}

      {/* Floating support launcher — WhatsApp and/or in-site chat */}
      {showWhatsApp && !isSupportApp && <Suspense fallback={null}><SupportWidget /></Suspense>}
    </>
  );
}

export default function AppWrapper() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Router>
          <SiteSettingsProvider>
            <GeoProvider>
              <App />
            </GeoProvider>
          </SiteSettingsProvider>
        </Router>
      </ErrorBoundary>
    </HelmetProvider>
  );
}
